import { useState, useEffect, useCallback, useMemo } from 'react'
import { API_BASE } from '@/settings/master'
import {
  Building2, ImagePlus, Loader, MapPin, Phone, Globe, Save,
  Briefcase, Hash, Mail, AlignLeft, Flag, Plus, X, Copy
} from 'lucide-react'
import Select from 'react-select'
import { motion, AnimatePresence } from 'framer-motion'
import { getorgInfo, updateorgInfo, getCountries } from '@/api/kuizy'

const emptyOrg = {
  name_th: '',
  name_en: '',
  business_type: '',
  email: '',
  phone: '',
  website: '',
  tax_id: '',
  addr1: '',
  addr2: '',
  sub_district: '',
  district: '',
  province: '',
  postal_code: '',
  country: '',
  description: '',
  logo_url: ''
}

const newBilling = () => ({
  id: '',
  organization_name: '',
  contact_name: '',
  tax_id: '',
  email: '',
  phone: '',
  addr1: '',
  addr2: '',
  sub_district: '',
  district: '',
  province: '',
  postal_code: '',
  country: '',
  is_default: false
})

export default function OrganizationInfo() {
  const roles = JSON.parse(localStorage.getItem('roles') || '[]')
  if (!roles.includes('MASTER_ADMIN'))
    return <div className="p-6 text-center text-red-600 font-semibold">คุณไม่มีสิทธิ์เข้าหน้านี้</div>

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [countries, setCountries] = useState([])
  const [logoFile, setLogoFile] = useState(null)
  const [form, setForm] = useState(emptyOrg)
  const [billing, setBilling] = useState([])

  const [cloneOrgName, setCloneOrgName] = useState('')
  const [cloneContactName, setCloneContactName] = useState('')

  const init = useCallback(async () => {
    try {
      const [cinfo, ctry] = await Promise.all([getorgInfo(), getCountries()])
      const d = cinfo.data || {}
      setForm(f => ({ ...f, ...d }))
      setBilling(Array.isArray(d.billing_addresses) ? d.billing_addresses : [])
      setCountries(ctry.data || [])
      const defName = (d.name_th || d.name_en || '').trim()
      setCloneOrgName(defName)
      setCloneContactName('')
    } catch {
      setErr('โหลดข้อมูลล้มเหลว')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { init() }, [init])

  const optCountries = useMemo(() => [
    { value: '', label: '—' },
    ...countries.map(c => ({ value: c.iso2, label: c.name_en }))
  ], [countries])

  const handleInput = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleFile = e => {
    const file = e.target.files?.[0]
    if (!file) return
    const img = new Image()
    img.src = URL.createObjectURL(file)
    img.onload = () => {
      const MAX = 300
      let w = img.width, h = img.height
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX } else { w = Math.round(w * MAX / h); h = MAX }
      }
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      canvas.toBlob(b => {
        const resized = new File([b], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
        setLogoFile(resized)
        handleInput('logo_url', URL.createObjectURL(resized))
      }, 'image/jpeg', 0.85)
    }
  }

  const srcLogo = form.logo_url
    ? /^(blob:|data:|http)/.test(form.logo_url) ? form.logo_url : `${API_BASE}${form.logo_url}`
    : ''

  const addBilling = (prefillFromOrg = false) => {
    const row = newBilling()
    if (prefillFromOrg) {
      row.organization_name = (cloneOrgName || form.name_th || form.name_en || '').trim()
      row.contact_name = cloneContactName || ''
      row.tax_id = form.tax_id || ''
      row.email = form.email || ''
      row.phone = form.phone || ''
      row.addr1 = form.addr1 || ''
      row.addr2 = form.addr2 || ''
      row.sub_district = form.sub_district || ''
      row.district = form.district || ''
      row.province = form.province || ''
      row.postal_code = form.postal_code || ''
      row.country = form.country || ''
    }
    setBilling(b => [...b, row])
  }

  const updateBilling = (idx, k, v) => {
    setBilling(b => {
      const arr = [...b]
      arr[idx] = { ...arr[idx], [k]: v }
      return arr
    })
  }

  const removeBilling = idx => {
    setBilling(b => b.filter((_, i) => i !== idx))
  }

  const copyOrgToBilling = idx => {
    const name = (cloneOrgName || form.name_th || form.name_en || '').trim()
    updateBilling(idx, 'organization_name', name)
    updateBilling(idx, 'contact_name', cloneContactName || '')
    updateBilling(idx, 'tax_id', form.tax_id || '')
    updateBilling(idx, 'email', form.email || '')
    updateBilling(idx, 'phone', form.phone || '')
    updateBilling(idx, 'addr1', form.addr1 || '')
    updateBilling(idx, 'addr2', form.addr2 || '')
    updateBilling(idx, 'sub_district', form.sub_district || '')
    updateBilling(idx, 'district', form.district || '')
    updateBilling(idx, 'province', form.province || '')
    updateBilling(idx, 'postal_code', form.postal_code || '')
    updateBilling(idx, 'country', form.country || '')
  }

  const save = async () => {
    setSaving(true)
    setErr('')
    try {
      const fd = new FormData()
      const keys = [
        "name_th", "name_en", "business_type", "email", "phone", "website",
        "tax_id", "addr1", "addr2", "sub_district", "district", "province", "postal_code", "country", "description"
      ]
      keys.forEach(k => fd.append(k, form[k] || ''))
      if (logoFile) fd.append('logo', logoFile)
      const cleanBilling = billing.map(x => ({
        id: x.id || undefined,
        organization_name: x.organization_name || '',
        contact_name: x.contact_name || '',
        tax_id: x.tax_id || '',
        email: x.email || '',
        phone: x.phone || '',
        addr1: x.addr1 || '',
        addr2: x.addr2 || '',
        sub_district: x.sub_district || '',
        district: x.district || '',
        province: x.province || '',
        postal_code: x.postal_code || '',
        country: x.country || '',
        is_default: !!x.is_default
      }))
      fd.append('billing_json', JSON.stringify(cleanBilling))
      const { data } = await updateorgInfo(fd)
      if (data?.logo_url) handleInput('logo_url', data.logo_url)
      if (Array.isArray(data?.billing_addresses)) setBilling(data.billing_addresses)
      setLogoFile(null)
    } catch {
      setErr('บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="max-w-6xl mx-auto p-6 flex flex-col gap-6">
      <header className="flex items-center gap-2 text-xl font-semibold text-primary"><Building2 size={20} /> จัดการข้อมูลองค์กร</header>
      {loading ? (
        <div className="flex justify-center py-20"><Loader className="animate-spin" /></div>
      ) : (
        <>
          <form onSubmit={e => { e.preventDefault(); save() }} className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-6">
            <div className="flex flex-col items-center gap-3">
              <label className="w-[140px] h-[140px] border border-gray-300 rounded-full flex items-center justify-center relative overflow-hidden cursor-pointer hover:border-primary group">
                {srcLogo ? <img src={srcLogo} alt="logo" className="object-cover w-full h-full" /> : <ImagePlus className="text-gray-400 group-hover:text-primary" size={36} />}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
              </label>
              <span className="text-xs text-gray-500">คลิกเพื่ออัปโหลดโลโก้</span>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-1"><Building2 size={14} /> ชื่อองค์กร (TH)</label>
                <input value={form.name_th} onChange={e => handleInput('name_th', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-1"><Building2 size={14} /> Organization Name (EN)</label>
                <input value={form.name_en} onChange={e => handleInput('name_en', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-1"><Briefcase size={14} /> ประเภทธุรกิจ</label>
                <Select classNamePrefix="rs" options={[
                  { value: '', label: '—' },
                  { value: 'EDU', label: 'สถาบันศึกษา/โรงเรียน' },
                  { value: 'CORP', label: 'บริษัท / ธุรกิจ' },
                  { value: 'ORG', label: 'หน่วยงาน / องค์กร' },
                  { value: 'PERSON', label: 'บุคคลทั่วไป' }
                ]} value={{
                  value: form.business_type || '',
                  label: ({ 'EDU': 'สถาบันศึกษา/โรงเรียน', 'CORP': 'บริษัท / ธุรกิจ', 'ORG': 'หน่วยงาน / องค์กร', 'PERSON': 'บุคคลทั่วไป' }[form.business_type] || '—')
                }} onChange={o => handleInput('business_type', o.value)} isSearchable />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-1"><Hash size={14} /> เลขประจำตัวผู้เสียภาษี (องค์กร)</label>
                <input value={form.tax_id} onChange={e => handleInput('tax_id', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-1"><Mail size={14} /> อีเมลติดต่อ</label>
                <input type="email" value={form.email} onChange={e => handleInput('email', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-1"><Phone size={14} /> โทรศัพท์</label>
                <input value={form.phone} onChange={e => handleInput('phone', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
              </div>
              <div className="flex flex-col gap-1 lg:col-span-2">
                <label className="text-sm font-medium flex items-center gap-1"><Globe size={14} /> เว็บไซต์</label>
                <input value={form.website} onChange={e => handleInput('website', e.target.value)} placeholder="https://" className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-sm font-semibold mb-3 flex items-center gap-2"><MapPin size={16} /> ที่อยู่องค์กร</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <input placeholder="ที่อยู่บรรทัด 1" value={form.addr1} onChange={e => handleInput('addr1', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
                </div>
                <div className="md:col-span-2">
                  <input placeholder="ที่อยู่บรรทัด 2" value={form.addr2} onChange={e => handleInput('addr2', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
                </div>
                <input placeholder="ตำบล/แขวง" value={form.sub_district} onChange={e => handleInput('sub_district', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
                <input placeholder="อำเภอ/เขต" value={form.district} onChange={e => handleInput('district', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
                <input placeholder="จังหวัด" value={form.province} onChange={e => handleInput('province', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
                <input placeholder="รหัสไปรษณีย์" value={form.postal_code} onChange={e => handleInput('postal_code', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm" />
                <Select classNamePrefix="rs" options={optCountries} value={optCountries.find(o => o.value === form.country) || optCountries[0]} onChange={o => handleInput('country', o.value)} isSearchable />
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><AlignLeft size={16} /> รายละเอียดเพิ่มเติม</div>
              <textarea rows={3} value={form.description} onChange={e => handleInput('description', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm resize-y" />
            </div>

            {err && <div className="text-red-600 text-sm">{err}</div>}
            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-50">
                {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
                บันทึก
              </button>
            </div>
          </form>

          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-4">
            <div className="text-sm font-semibold flex items-center gap-2"><MapPin size={16} /> ที่อยู่วางบิล/ใบเสร็จ</div>


            <div className="flex flex-col gap-4">
              <AnimatePresence initial={false}>
                {billing.map((row, idx) => (
                  <motion.div key={row.id || `idx-${idx}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="rounded-lg border border-gray-200 p-4">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm">ชื่อองค์กรบนใบวางบิล</label>
                        <input value={row.organization_name || ''} onChange={e => updateBilling(idx, 'organization_name', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm">ผู้ติดต่อ</label>
                        <input value={row.contact_name || ''} onChange={e => updateBilling(idx, 'contact_name', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm">เลขผู้เสียภาษี</label>
                        <input value={row.tax_id || ''} onChange={e => updateBilling(idx, 'tax_id', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm">อีเมล</label>
                        <input value={row.email || ''} onChange={e => updateBilling(idx, 'email', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm">โทรศัพท์</label>
                        <input value={row.phone || ''} onChange={e => updateBilling(idx, 'phone', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3 mt-3">
                      <div className="md:col-span-2">
                        <input placeholder="ที่อยู่บรรทัด 1" value={row.addr1 || ''} onChange={e => updateBilling(idx, 'addr1', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="md:col-span-2">
                        <input placeholder="ที่อยู่บรรทัด 2" value={row.addr2 || ''} onChange={e => updateBilling(idx, 'addr2', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                      </div>
                      <input placeholder="ตำบล/แขวง" value={row.sub_district || ''} onChange={e => updateBilling(idx, 'sub_district', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      <input placeholder="อำเภอ/เขต" value={row.district || ''} onChange={e => updateBilling(idx, 'district', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      <input placeholder="จังหวัด" value={row.province || ''} onChange={e => updateBilling(idx, 'province', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      <input placeholder="รหัสไปรษณีย์" value={row.postal_code || ''} onChange={e => updateBilling(idx, 'postal_code', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      <Select classNamePrefix="rs" options={optCountries} value={optCountries.find(o => o.value === row.country) || optCountries[0]} onChange={o => updateBilling(idx, 'country', o.value)} isSearchable />
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="accent-primary" checked={!!row.is_default} onChange={e => updateBilling(idx, 'is_default', e.target.checked)} />
                        ตั้งเป็นที่อยู่เริ่มต้น
                      </label>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => copyOrgToBilling(idx)} className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded px-3 py-1.5 text-sm"><Copy size={14} /> คัดลอกจากที่อยู่องค์กร</button>
                        <button type="button" onClick={() => removeBilling(idx)} className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-red-600 rounded px-3 py-1.5 text-sm"><X size={14} /> ลบ</button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              <div className="flex justify-center">
                <div className="flex gap-2">
                  <button onClick={() => addBilling(false)} className="inline-flex items-center gap-1 bg-primary text-white rounded-lg px-4 py-2 text-sm hover:bg-secondary"><Plus size={16} /> เพิ่มที่อยู่วางบิล</button>
                  <button onClick={() => addBilling(true)} className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-lg px-4 py-2 text-sm"><Plus size={16} /> เพิ่มและคัดลอกที่อยู่องค์กร</button>
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-50">
                  {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
                  บันทึกที่อยู่วางบิล
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </motion.div>
  )
}
